import React, { useMemo } from 'react'
import { useLocation, useRouteMatch } from 'react-router'

import Dropdown from 'components/Dropdown'
import Tooltip from 'components/Tooltip'
import WorkspaceLogo from 'modules/workspace/components/WorkspaceLogo'
import {
  DropdownChevrons,
  DropdownContentContainer,
  DropdownDivider,
  DropdownLinkItem,
  DropdownTarget,
  SelectedWorkspaceName,
  SignedOutLabel,
  SsoTag,
  WorkspaceName,
  ExpiredLabel,
  DropdownHeader,
  DrodpwonItemSubtitle,
} from './WorkspacesDropdown.styles'

// Hooks
import { useGetInitialUserQuery, WorkspaceMinimalFragment } from 'gqlTypes'
import {
  SignInWithPasswordUser,
  useSignInToUnauthorizedWorkspace,
  useUserAuthorizations,
} from 'modules/sso/operations'
import { useIsNodeContentTruncated } from 'utils/hooks/useIsNodeContentTruncated'
import { useForTablet } from 'components/Breakpoint'

// Types
import { PopoverChildrenProps } from 'components/Popover'

// Utils
import { routes } from 'constants/routes'
import {
  isPersonalWorkspace,
  getPersonalWorkspace,
  getMemberWorkspaces,
  getGuestWorkspaces,
} from 'modules/workspace/utils'

/**
 * TYPES
 */
export interface WorkspacesDropdownProps {
  arrowStyle?: React.CSSProperties
  workspace: WorkspaceMinimalFragment
  workspaces: WorkspaceMinimalFragment[]
}

interface WorkspaceDropdownItemProps {
  selectedWorkspace: WorkspaceMinimalFragment
  workspace: WorkspaceMinimalFragment
  user: SignInWithPasswordUser
}

/**
 * COMPONENTS HELPERS
 */
const DropdownWrapper = ({
  toggle,
  children,
  ...props
}: {
  toggle: PopoverChildrenProps
  children: React.ReactNode
}) => (
  <Dropdown
    placement="bottom"
    minWidth="initial"
    modifiers={[{ name: 'offset' }]}
    style={{
      /* Prevent the dropdown container to expand and hide the text-overflow */
      overflow: 'hidden',
    }}
    contentPadding="0"
    toggle={toggle}
    {...props}>
    {children}
  </Dropdown>
)

const WorkspaceDropdownItem = ({
  workspace,
  selectedWorkspace,
  user,
}: WorkspaceDropdownItemProps) => {
  const location = useLocation()
  const isWorkspaceRoute = useRouteMatch({
    path: routes.WORKSPACE.template(),
  })
  const isWorkspaceProjectRoute = useRouteMatch({
    path: routes.WORKSPACE_PROJECT.template(),
  })

  const { hasAccessToWorkspace } = useUserAuthorizations()
  const { signIn } = useSignInToUnauthorizedWorkspace()
  const [
    isTextTruncated,
    truncatedNodeRef,
  ] = useIsNodeContentTruncated<HTMLDivElement>()
  const isTabletAndBigger = useForTablet()

  // If the device is too narrow, the tooltip doesn't fit in the screen
  // and looks broken.
  const shouldShowTooltip = isTabletAndBigger && isTextTruncated

  if (!workspace) {
    return null
  }

  const { identifier, avatar, name } = workspace
  const { ssoEnabled, ssoStartUrl } = workspace.customer || {}

  const isSelectedWorkspace = identifier === selectedWorkspace.identifier
  const isAuthorizedWorkspace = hasAccessToWorkspace(workspace)
  const isWorkspaceActive = workspace.status === 'ACTIVE'
  const isGuestWorkspace = workspace.userRole === 'GUEST'

  let workspaceRoute
  if (workspace.userRole === 'FINANCE') {
    // Finance role user does not have access to documents, so it should
    // start with workspace settings
    workspaceRoute = routes.WORKSPACE_SETTINGS_GENERAL.create({
      workspaceId: identifier,
    })
  } else {
    // Mantain the pathname when changing between workspaces routes
    if (isWorkspaceRoute && !isWorkspaceProjectRoute) {
      workspaceRoute = location.pathname.replace(
        selectedWorkspace.identifier,
        identifier
      )
    } else {
      workspaceRoute = routes.WORKSPACE_SHARES.create({
        workspaceId: identifier,
      })
    }
  }

  return (
    <Tooltip content={name} placement="right" disabled={!shouldShowTooltip}>
      <DropdownLinkItem
        onClick={event => {
          // If is authorized then we use the `to` route
          if (isAuthorizedWorkspace) return

          event.preventDefault()
          const redirectBackToCurrentUrl = false
          signIn(
            {
              workspaceId: identifier,
              workspaceName: name,
              ssoEnabled,
              ssoStartUrl: ssoStartUrl || undefined,
            },
            { ...user, isGuestWorkspace },
            redirectBackToCurrentUrl
          )
        }}
        to={workspaceRoute}
        isActive={() => isSelectedWorkspace}
        $expired={!isWorkspaceActive}>
        {isPersonalWorkspace(workspace) ? (
          <WorkspaceLogo.Icon icon="briefcase" size="24px" UNSAFE_ADD_MARGIN />
        ) : (
          <WorkspaceLogo
            workspaceName={name}
            src={avatar?.large}
            size="24px"
            UNSAFE_ADD_MARGIN
          />
        )}

        <WorkspaceName ref={truncatedNodeRef}>{name}</WorkspaceName>
        {!isWorkspaceActive && <ExpiredLabel />}
        {!isAuthorizedWorkspace && (
          <SignedOutLabel expired={!isWorkspaceActive} />
        )}
        {/* Guest users don't have access to SSO information */}
        {ssoEnabled && !isGuestWorkspace && <SsoTag />}
      </DropdownLinkItem>
    </Tooltip>
  )
}

/**
 * MAIN COMPONENT
 */
export const WorkspacesDropdown: React.FC<WorkspacesDropdownProps> = ({
  workspace,
  workspaces,
  ...props
}) => {
  const { data } = useGetInitialUserQuery()

  const hasPersonalIdentity = data?.me.hasPersonalIdentity

  const user: SignInWithPasswordUser = {
    name: data?.me?.name ?? '',
    email: data?.me?.email ?? '',
    avatarSrc: data?.me?.avatar?.small ?? '',
  }

  const [personalWorkspace, memberWorkspaces, guestWorkspaces] = useMemo(() => {
    const personalWorkspace = getPersonalWorkspace(workspaces)
    const memberWorkspaces = getMemberWorkspaces(workspaces)
    const guestWorkspaces = getGuestWorkspaces(workspaces)

    return [personalWorkspace, memberWorkspaces, guestWorkspaces] as const
  }, [workspaces])

  return (
    <DropdownWrapper
      {...props}
      toggle={
        <DropdownTarget>
          {isPersonalWorkspace(workspace) ? (
            <WorkspaceLogo.Icon
              icon="briefcase"
              size="24px"
              UNSAFE_ADD_MARGIN
            />
          ) : (
            <WorkspaceLogo
              workspaceName={workspace.name}
              src={workspace.avatar?.large}
              size="24px"
              UNSAFE_ADD_MARGIN
            />
          )}
          <SelectedWorkspaceName>{workspace.name}</SelectedWorkspaceName>
          <DropdownChevrons />
        </DropdownTarget>
      }>
      <DropdownContentContainer data-testid="workspace-dropdown">
        {/* Personal workspace */}
        {personalWorkspace && (
          <>
            <WorkspaceDropdownItem
              key={personalWorkspace.identifier}
              selectedWorkspace={workspace}
              workspace={personalWorkspace}
              user={user}
            />
            <DropdownDivider />
          </>
        )}

        {/* Member workspaces */}
        {memberWorkspaces.length > 0 && (
          <DropdownHeader>Member of</DropdownHeader>
        )}
        {memberWorkspaces.map(workspaceItem => (
          <WorkspaceDropdownItem
            key={workspaceItem.identifier}
            selectedWorkspace={workspace}
            workspace={workspaceItem}
            user={user}
          />
        ))}

        {memberWorkspaces.length > 0 && guestWorkspaces.length > 0 && (
          <DropdownDivider />
        )}

        {/* Guest workspaces */}
        {guestWorkspaces.length > 0 && (
          <DropdownHeader>Guest in</DropdownHeader>
        )}
        {guestWorkspaces.map(workspaceItem => (
          <WorkspaceDropdownItem
            key={workspaceItem.identifier}
            selectedWorkspace={workspace}
            workspace={workspaceItem}
            user={user}
          />
        ))}

        {/* Workspace creation */}
        {hasPersonalIdentity && (
          <>
            <DropdownDivider />
            <DropdownLinkItem to={routes.WORKSPACE_CREATE.create({})}>
              <WorkspaceLogo.Icon size="24px" icon="plus" UNSAFE_ADD_MARGIN />
              <div>
                Create Workspace
                <DrodpwonItemSubtitle>
                  30-Day trial available
                </DrodpwonItemSubtitle>
              </div>
            </DropdownLinkItem>
          </>
        )}
      </DropdownContentContainer>
    </DropdownWrapper>
  )
}
